/**
 * Google sign-in signup detection.
 *
 * The browser Supabase client uses the implicit flow, so the OAuth callback
 * route never receives a `code` and cannot tell a new account from a returning
 * one. The browser instead recognises a Google account created moments ago and
 * uses a per-browser account marker and a deterministic event UUID so PostHog
 * can collapse a duplicate. That UUID also identifies the signup conversion in
 * the data layer; the GTM signup tag must map it to Google's Transaction ID.
 */

export const NEW_ACCOUNT_SIGNUP_WINDOW_MS = 10 * 60 * 1000;
const SIGNUP_CAPTURE_KEY_PREFIX = "chicky_signup_capture_v2:";

type SignupCandidateUser = {
  id: string;
  created_at?: string | null;
  app_metadata?: { provider?: string | null } | null;
};

export function isRecentlyCreatedGoogleAccount(
  user: SignupCandidateUser,
  now = Date.now()
): boolean {
  if (user.app_metadata?.provider !== "google" || !user.created_at) return false;

  const createdAtMs = Date.parse(user.created_at);
  // Allow modest clock skew in either direction between the device and Supabase.
  return (
    Number.isFinite(createdAtMs) && Math.abs(now - createdAtMs) <= NEW_ACCOUNT_SIGNUP_WINDOW_MS
  );
}

/**
 * Deterministic RFC 9562 version-8 UUID for a (namespace, key) pair, derived
 * from a non-cryptographic 128-bit hash. It only needs to be stable and unique
 * per account, not secret.
 */
export function deterministicEventUuid(namespace: string, key: string): string {
  const input = `${namespace}:${key}`;
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let index = 0; index < input.length; index++) {
    const code = input.charCodeAt(index);
    h1 = h2 ^ Math.imul(h1 ^ code, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ code, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ code, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ code, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;

  const hex = [h1, h2, h3, h4].map((part) => (part >>> 0).toString(16).padStart(8, "0")).join("");
  const variant = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `8${hex.slice(13, 16)}`,
    `${variant}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join("-");
}

export function signupEventUuid(userId: string): string {
  return deterministicEventUuid("user_signed_up", userId);
}

// Reading `window.localStorage` itself can throw when storage is disabled.
function browserLocalStorage(): Storage | undefined {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function hasCapturedSignup(userId: string, storage = browserLocalStorage()): boolean {
  try {
    return !!storage?.getItem(`${SIGNUP_CAPTURE_KEY_PREFIX}${userId}`);
  } catch {
    return false;
  }
}

export function markSignupCaptured(
  userId: string,
  storage = browserLocalStorage(),
  now = Date.now()
): boolean {
  try {
    if (!storage) return false;
    storage.setItem(`${SIGNUP_CAPTURE_KEY_PREFIX}${userId}`, String(now));
    return true;
  } catch {
    return false;
  }
}
